/**
 * Mantine Hook
 * Documentation found here: https://mantine.dev/hooks/use-debounced-value/
 */
import { useEffect, useRef, useState } from 'react';

export function useDebouncedValue<T = unknown>(
  newValue: T,
  wait: number,
  options = { leading: false }
): [T, () => void] {
  const [value, setValue] = useState(newValue);
  const mountedRef = useRef(false);
  const timeoutRef = useRef<number | null>(null);
  const cooldownRef = useRef(false);

  const cancel = () => window.clearTimeout(timeoutRef.current!);

  useEffect(() => {
    if (mountedRef.current) {
      if (!cooldownRef.current && options.leading) {
        cooldownRef.current = true;
        setValue(newValue);
      } else {
        cancel();
        timeoutRef.current = window.setTimeout(() => {
          cooldownRef.current = false;
          setValue(newValue);
        }, wait);
      }
    }
  }, [newValue, options.leading, wait]);

  useEffect(() => {
    mountedRef.current = true;
    return cancel;
  }, []);

  return [value, cancel];
}
